import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Switch,
  TextField,
  Tooltip
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  settingsPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  settingsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  settingsText: {
    flex: 1,
    minWidth: 240,
  },
  settingsField: {
    width: 150,
  },
  // Adicione um estilo para a box vermelha
  redBox: {
    backgroundColor: "#ffcccc", // Definindo a cor de fundo vermelha
    padding: theme.spacing(2), // Adicionando um espaçamento interno
    marginBottom: theme.spacing(2), // Adicionando margem inferior para separar do conteúdo abaixo
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];

    prompts.forEach((prompt) => {
      const promptIndex = state.findIndex((p) => p.id === prompt.id);
      if (promptIndex !== -1) {
        state[promptIndex] = prompt;
      } else {
        newPrompts.push(prompt);
      }
    });

    return [...state, ...newPrompts];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);

    if (promptIndex !== -1) {
      state[promptIndex] = prompt;
      return [...state];
    } else {
      return [prompt, ...state];
    }
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const promptIndex = state.findIndex((p) => p.id === promptId);
    if (promptIndex !== -1) {
      state.splice(promptIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Prompts = () => {
  const classes = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  // Pausa da IA após resposta do atendente: vale para TODOS os agentes da empresa, por
  // isso fica aqui e não no modal de um agente.
  const [humanCooldown, setHumanCooldown] = useState("");
  const [savedHumanCooldown, setSavedHumanCooldown] = useState("");
  const [savingCooldown, setSavingCooldown] = useState(false);
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;
  const isAdmin = user.profile === "admin";

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error(i18n.t("common.featureNotEnabled"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompts/ai-settings");
        const value = String(data?.humanCooldownMinutes ?? "");
        setHumanCooldown(value);
        setSavedHumanCooldown(value);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-prompt`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [companyId, socketManager]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  const handleSaveHumanCooldown = async () => {
    const minutes = parseInt(humanCooldown, 10);

    if (Number.isNaN(minutes) || minutes < 0) {
      toast.error(i18n.t("prompts.humanCooldown.invalid"));
      return;
    }

    setSavingCooldown(true);
    try {
      const { data } = await api.put("/prompts/ai-settings", {
        humanCooldownMinutes: minutes,
      });
      const value = String(data?.humanCooldownMinutes ?? minutes);
      setHumanCooldown(value);
      setSavedHumanCooldown(value);
      toast.success(i18n.t("prompts.humanCooldown.saved"));
    } catch (err) {
      toastError(err);
    }
    setSavingCooldown(false);
  };

  const handleToggleAiHub = async (promptId, currentStatus) => {
    try {
      const { data } = await api.post(`/prompt/${promptId}/toggle-aihub`);
      
      if (data.success) {
        toast.success(
          data.aiHubEnabled 
            ? i18n.t("prompts.toasts.aiHubEnabled") 
            : i18n.t("prompts.toasts.aiHubDisabled")
        );
        
        // Atualiza o prompt no estado local
        dispatch({ 
          type: "UPDATE_PROMPTS", 
          payload: data.prompt 
        });
      }
    } catch (err) {
      toastError(err);
      console.error("Erro ao alternar AI Hub:", err);
    }
  };

  return (
    <MainContainer>
   

      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />
      <MainHeader>
        <Title>{i18n.t("prompts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenPromptModal}
          >
            {i18n.t("prompts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.settingsPaper} variant="outlined">
        <div className={classes.settingsRow}>
          <div className={classes.settingsText}>
            <Typography variant="subtitle2">
              {i18n.t("prompts.humanCooldown.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("prompts.humanCooldown.helper")}
            </Typography>
          </div>
          <TextField
            className={classes.settingsField}
            type="number"
            size="small"
            variant="outlined"
            disabled={!isAdmin}
            label={i18n.t("prompts.humanCooldown.label")}
            value={humanCooldown}
            onChange={(e) => setHumanCooldown(e.target.value)}
            inputProps={{ min: 0, max: 10080 }}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={
              !isAdmin || savingCooldown || humanCooldown === savedHumanCooldown
            }
            onClick={handleSaveHumanCooldown}
          >
            {i18n.t("prompts.humanCooldown.save")}
          </Button>
        </div>
      </Paper>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">
                {i18n.t("prompts.table.name")}
              </TableCell>
              <TableCell align="left">
                {i18n.t("prompts.table.agentId")}
              </TableCell>
              <TableCell align="left">
                {i18n.t("prompts.table.integrateHumanMessages")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("prompts.table.aiHub")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("prompts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {prompts.map((prompt) => (
                <TableRow key={prompt.id}>
                  <TableCell align="left">{prompt.name}</TableCell>
                  <TableCell align="left">{prompt.aiHubAgentId}</TableCell>
                  <TableCell align="left">
                    {prompt.integrateHumanMessages
                      ? i18n.t("prompts.integrations.enabled")
                      : i18n.t("prompts.integrations.disabled")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip 
                      title={
                        prompt.aiHubEnabled 
                          ? i18n.t("prompts.table.aiHubTooltip.enabled")
                          : i18n.t("prompts.table.aiHubTooltip.disabled")
                      }
                    >
                      <Switch
                        checked={prompt.aiHubEnabled || false}
                        onChange={() => handleToggleAiHub(prompt.id, prompt.aiHubEnabled)}
                        color="primary"
                        size="small"
                        disabled={!prompt.aiHubAgentId}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditPrompt(prompt)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedPrompt(prompt);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Prompts;
